/* ============================================================
   Slant Spartan launch page — scoped JS
   - Lenis smooth scroll
   - GSAP + ScrollTrigger registration
   - Reduced-motion respected (no Lenis, no scroll choreography)
   - Scene-level animations added in subsequent pushes
   ============================================================ */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    // === Fonts-ready promise ===
    // The dim hero background frame loads immediately so the page
    // builds intrigue right away. Only the hero title + designer
    // line are held back (opacity:0 in CSS) until web fonts are
    // ready — then JS fades them in so the title "pops up" in the
    // correct typeface, never swapping mid-animation. 800ms safety
    // timeout in case fonts never resolve.
    var fontsReady = new Promise(function (resolve) {
      var resolved = false;
      function done() { if (!resolved) { resolved = true; resolve(); } }
      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(done);
      } else {
        done();
      }
      setTimeout(done, 800);
    });

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Mobile breakpoint — below 720px we switch from pinned overlay
    // architecture to stacked image-above-text with scroll-into-view
    // reveals. Hero stays pinned on both; F2/F3 fork here.
    var isMobile = window.matchMedia('(max-width: 720px)').matches;

    // Deep-link scroll: ?goto=<element-id> scrolls to that section on load.
    // We use a QUERY PARAM (not a #hash) on purpose: email tools like Klaviyo
    // append their own params (utm_*, _kx) and can mangle a #fragment or drop
    // it through their click-tracking redirect. A query param always survives
    // and order doesn't matter. Sanitized to an id-safe token.
    var ssGoto = null;
    try {
      var _g = (new URLSearchParams(window.location.search)).get('goto');
      if (_g && /^[A-Za-z0-9_-]+$/.test(_g)) ssGoto = _g;
    } catch (e) {}
    function ssScrollToGoto() {
      if (!ssGoto) return;
      var el = document.getElementById(ssGoto);
      if (!el) return;
      if (window.__ssLenis && typeof window.__ssLenis.scrollTo === 'function') {
        window.__ssLenis.scrollTo(el, { offset: 0 });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    if (ssGoto) {
      // Run after load + a couple backstops so ScrollTrigger pins / fonts have
      // settled and the target's final position is correct.
      window.addEventListener('load', function () { setTimeout(ssScrollToGoto, 120); });
      setTimeout(ssScrollToGoto, 700);
      setTimeout(ssScrollToGoto, 1600);
    }

    // Skip-to-product: a fixed down-arrow shown while the buy section is still
    // ahead; click scrolls straight to it (releases the mobile hero lock first).
    var ssSkip = document.querySelector('[data-ss-skip]');
    var ssBuy = document.getElementById('buy');
    if (ssSkip && ssBuy) {
      ssSkip.addEventListener('click', function (e) {
        e.preventDefault();
        if (window.__ssReleaseHero) { try { window.__ssReleaseHero(); } catch (_e) {} }
        if (window.__ssLenis && typeof window.__ssLenis.scrollTo === 'function') window.__ssLenis.scrollTo(ssBuy, { offset: 0 });
        else ssBuy.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      var ssSkipVis = function () {
        ssSkip.classList.toggle('is-visible', ssBuy.getBoundingClientRect().top > window.innerHeight * 0.6);
      };
      ssSkipVis();
      window.addEventListener('scroll', ssSkipVis, { passive: true });
      window.addEventListener('resize', ssSkipVis);
      window.addEventListener('load', function () { setTimeout(ssSkipVis, 250); });
    }

    // Helper: stacked-layout reveal for F2/F3 on mobile. Image fades
    // in as the section enters viewport; headblock rises; body lines
    // cascade; kicker lands. Same DNA as desktop, scroll-driven.
    function setupMobileReveal(refs) {
      if (!window.gsap || !window.ScrollTrigger) return;
      if (refs.image) {
        window.gsap.to(refs.image, {
          scrollTrigger: { trigger: refs.image, start: 'top 95%', end: 'top 55%', scrub: 0.5 },
          opacity: 1,
          ease: 'none'
        });
      }
      if (refs.headblock) {
        window.gsap.fromTo(refs.headblock,
          { opacity: 0, y: 30 },
          {
            scrollTrigger: { trigger: refs.headblock, start: 'top 90%', end: 'top 60%', scrub: 0.5 },
            opacity: 1, y: 0, ease: 'power2.out'
          }
        );
      }
      if (refs.payoff) {
        window.gsap.fromTo(refs.payoff,
          { opacity: 0, y: 24 },
          {
            scrollTrigger: { trigger: refs.payoff, start: 'top 88%', end: 'top 58%', scrub: 0.5 },
            opacity: 1, y: 0, ease: 'power2.out'
          }
        );
      }
      if (refs.lines) {
        refs.lines.forEach(function (line) {
          window.gsap.fromTo(line,
            { opacity: 0, y: 24 },
            {
              scrollTrigger: { trigger: line, start: 'top 92%', end: 'top 72%', scrub: 0.4 },
              opacity: 1, y: 0, ease: 'power2.out'
            }
          );
        });
      }
      if (refs.kicker) {
        window.gsap.fromTo(refs.kicker,
          { opacity: 0, y: 24, scale: 0.97 },
          {
            scrollTrigger: { trigger: refs.kicker, start: 'top 90%', end: 'top 65%', scrub: 0.4 },
            opacity: 1, y: 0, scale: 1, ease: 'power3.out'
          }
        );
        // Brass highlight wipe on mobile too
        var hl = refs.kicker.querySelector('[data-ss-hl]');
        if (hl) {
          window.gsap.fromTo(hl,
            { '--ss-hl-w': '0%' },
            {
              scrollTrigger: { trigger: refs.kicker, start: 'top 90%', end: 'top 65%', scrub: 0.4 },
              '--ss-hl-w': '100%',
              ease: 'power2.inOut'
            }
          );
        }
      }
    }

    // --- Lenis smooth scroll — DESKTOP ONLY ---
    // Lenis maintains its own scroll state and fights native scrolling after
    // an iOS pinch-zoom desyncs the visual viewport, which locked the page
    // (can zoom, can't scroll). iOS Safari ignores user-scalable=no, so we
    // can't disable zoom — instead we drop Lenis on mobile entirely. Native
    // scroll + ScrollTrigger (which listens to native scroll) handle mobile.
    if (typeof window.Lenis !== 'undefined' && !reduced && !isMobile) {
      var lenis = new window.Lenis({
        duration: 1.1,
        easing: function (t) { return 1 - Math.pow(1 - t, 3); }, // easeOutCubic
        smoothWheel: true,
        smoothTouch: false
      });

      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
      window.__ssLenis = lenis;
    }

    // --- GSAP + ScrollTrigger ---
    if (typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined') {
      window.gsap.registerPlugin(window.ScrollTrigger);

      if (window.__ssLenis) {
        window.__ssLenis.on('scroll', window.ScrollTrigger.update);
      }

      // === HERO: pinned scrub-reveal driven by frame sequence ===
      var heroEl = document.querySelector('.ss-launch-hero');
      var heroOverlay = document.querySelector('[data-ss-hero-overlay]');
      var heroBridgeBeats = document.querySelectorAll('[data-ss-bridge-beat]');
      var heroF1Image = document.querySelector('[data-ss-hero-feature1-image]');
      var heroF1Heading = document.querySelector('[data-ss-hero-feature1-heading]');
      var heroF1Subtitle = document.querySelector('[data-ss-hero-feature1-subtitle]');
      var heroCanvas = document.querySelector('[data-ss-hero-canvas]');
      var heroFramesScript = document.querySelector('[data-ss-hero-frames]');
      var heroFrameUrls = [];
      if (heroFramesScript) {
        try { heroFrameUrls = JSON.parse(heroFramesScript.textContent); } catch (e) {}
      }
      // Mobile-only hero frames (purpose-built vertical 9:16). Falls back to
      // the desktop frames if this list isn't present.
      var heroFramesMobileScript = document.querySelector('[data-ss-hero-frames-mobile]');
      var heroFrameUrlsMobile = [];
      if (heroFramesMobileScript) {
        try { heroFrameUrlsMobile = JSON.parse(heroFramesMobileScript.textContent); } catch (e) {}
      }

      if (heroEl && !reduced) {
        // ╔══════════════════════════════════════════════════════════╗
        // ║  HERO PIN — LOCKED. Do not modify.                       ║
        // ║  Pins hero for its natural 700vh. F1 reveal animations   ║
        // ║  (80–99%) all anchor to this trigger's percentages, and  ║
        // ║  extending the pin breaks those references.              ║
        // ║                                                          ║
        // ║  Continuity into F2 is handled by an F1 CARRYOVER inside ║
        // ║  the F2 pin (see ss-launch-features.liquid). When this   ║
        // ║  pin releases at scrollY=700vh, F2's pin engages at the  ║
        // ║  same scrollY with the carryover already visible in the  ║
        // ║  identical position — the visual hand-off is silent.     ║
        // ╚══════════════════════════════════════════════════════════╝
        var heroPinEl = document.querySelector('.ss-launch-hero__pin');
        // DESKTOP ONLY. On mobile the hero is a scroll-locked,
        // gesture-stepped sequence (see MOBILE HERO controller below) —
        // it is NOT pinned to a tall scroll region.
        if (heroPinEl && !isMobile) {
          window.ScrollTrigger.create({
            trigger: heroEl,
            start: 'top top',
            end: 'bottom top',
            pin: heroPinEl,
            pinSpacing: false
          });
        }

        // Initial entrance: title + designer line settle in only AFTER
        // web fonts load. Held at opacity:0 in CSS so there's no flash
        // in a system font. Larger stagger so the designer line lands
        // as a deliberate second beat, not a quick tail-on.
        if (heroOverlay) {
          fontsReady.then(function () {
            window.gsap.fromTo(heroOverlay.children,
              { y: 24, opacity: 0 },
              {
                y: 0,
                opacity: 1,
                duration: 0.9,
                ease: 'power2.out',
                stagger: 0.5,
                delay: 0.1
              }
            );
          });
        }

        // ===========================================================
        // HERO CHOREOGRAPHY v7 — GSAP pinned, slower crossfades
        // Hero is 700vh. Pin is GSAP-driven, holds for full section duration.
        // Crossfades are 6% with power2 eases — dominant beat stays bright.
        // Holds are 9% each — substantial landing target per beat.
        //
        //   0–7%   : title fades + lifts off
        //   0–16%  : frames scrub from 1 to 32
        //  16–19%  : Beat 1 "Ten years of refinement." SLIDES in (only entrance)
        //  19–28%  : Beat 1 HOLD
        //  28–34%  : CROSSFADE — Beat 1 ↔ Beat 2 (6%)
        //  34–43%  : Beat 2 "One design." HOLD
        //  43–49%  : CROSSFADE — Beat 2 ↔ Beat 3 (6%, same as 1↔2 and 3↔4)
        //  49–58%  : Beat 3 "Only 320 made." HOLD
        //  58–64%  : CROSSFADE — Beat 3 ↔ Beat 4 (6%)
        //  64–73%  : Beat 4 "Each one serialized." HOLD
        //  73–76%  : Beat 4 RISES out (only exit — sets up F1 delay)
        //  80–86%  : F1 headblock (eyebrow + heading) SCROLLS UP from below
        //              AND F1 image fades IN / canvas fades OUT (same window)
        //              — image transition is complete the moment heading lands
        //  86–89%  : Body line 1 "240 grams of G10 and machined brass," rises
        //  88–91%  : Body line 2 "balanced at your grip." rises (cascading overlap)
        //  90–93%  : Body line 3 "Your draw slows." rises
        //  92–95%  : Body line 4 "Your hand stops drifting." rises
        //  95–99%  : Kicker "You stop throwing shots..." rises with weight
        // ===========================================================

        // Title: fade + lift OFF — DESKTOP ONLY (mobile drives the
        // title fade via the gesture-stepped timeline).
        if (heroOverlay && !isMobile) {
          window.gsap.to(heroOverlay, {
            scrollTrigger: {
              trigger: heroEl,
              start: 'top top',
              end: '7% top',
              scrub: 0.3
            },
            opacity: 0,
            y: -60,
            ease: 'power2.in'
          });
        }

        // Bridge beats — tighter 6% crossfades with power2 eases so the
        // dominant beat stays present longer at each edge of the dissolve.
        // Longer 9% holds give each beat a real landing target before the
        // next one arrives.
        //   slide: rises from below (y:40 → 0), eased out
        //   fade:  opacity only, eased (power2.in out / power2.out in)
        //   rise:  exits upward (y:0 → -40), eased in
        var beatChoreo = [
          // Beat 1: slide in (entrance), fade out (crossfade with Beat 2)
          { inStart: 16, inEnd: 19, inType: 'slide',
            outStart: 28, outEnd: 34, outType: 'fade' },
          // Beat 2: fade in (crossfade from Beat 1), fade out (crossfade with Beat 3)
          { inStart: 28, inEnd: 34, inType: 'fade',
            outStart: 43, outEnd: 49, outType: 'fade' },
          // Beat 3: fade in (crossfade from Beat 2), fade out (crossfade with Beat 4)
          { inStart: 43, inEnd: 49, inType: 'fade',
            outStart: 58, outEnd: 64, outType: 'fade' },
          // Beat 4: fade in (crossfade from Beat 3), rise out (exit before F1 delay)
          { inStart: 58, inEnd: 64, inType: 'fade',
            outStart: 73, outEnd: 76, outType: 'rise' }
        ];
        // DESKTOP ONLY — the entire scroll-scrubbed bridge choreography.
        // On mobile the bridges are driven by the gesture-stepped
        // timeline in the MOBILE HERO controller below.
        if (!isMobile) heroBridgeBeats.forEach(function (beat, i) {
          var c = beatChoreo[i];
          if (!c) return;

          // IN
          var fromState = c.inType === 'slide'
            ? { opacity: 0, y: 40 }
            : { opacity: 0, y: 0 };
          // Slide-in: settle into place. Crossfade-in: quick rise to dominance.
          var inEase = c.inType === 'slide' ? 'power2.out' : 'power2.out';
          window.gsap.fromTo(beat, fromState, {
            scrollTrigger: {
              trigger: heroEl,
              start: c.inStart + '% top',
              end: c.inEnd + '% top',
              scrub: 0.3
            },
            opacity: 1,
            y: 0,
            ease: inEase
          });

          // OUT — skip for bridge 4 (i === 3) on mobile. On mobile we
          // want bridge 4 ("Each one serialized") to STAY ON SCREEN
          // until the F1 mobile section scrolls up over it. No exit
          // animation at all.
          if (i === 3 && isMobile) return;

          var outY = c.outType === 'rise' ? -40 : 0;
          // Rise-out: accelerating exit. Crossfade-out: stays bright until late.
          var outEase = c.outType === 'rise' ? 'power2.in' : 'power2.in';
          window.gsap.to(beat, {
            scrollTrigger: {
              trigger: heroEl,
              start: c.outStart + '% top',
              end: c.outEnd + '% top',
              scrub: 0.3
            },
            opacity: 0,
            y: outY,
            ease: outEase
          });
        });

        // F1 INSIDE-HERO animations — DESKTOP ONLY. On mobile the
        // in-hero F1 is hidden via CSS and replaced by the standalone
        // ss-launch-mobile-f1 section in normal flow. Skipping these
        // also keeps the hero canvas (slingshot) from fading out at
        // hero 76-82% on mobile — the slingshot stays visible until
        // the F1 mobile section slides up to cover it.
        if (!isMobile) {

        // F1 heading: scrolls UP from the bottom of the screen, OVER the still-locked
        // final hero frame (76–82%). The headline lands by 82% and then HOLDS
        // alone until 88% — a deliberate 6% breath so the viewer can read
        // "Heavy." before the payoff and body cascade arrive.
        if (heroF1Heading) {
          window.gsap.fromTo(heroF1Heading,
            { opacity: 0, y: '55vh' },
            {
              scrollTrigger: {
                trigger: heroEl,
                start: '76% top',
                end: '82% top',
                scrub: 0.3
              },
              opacity: 1,
              y: 0,
              ease: 'power2.out'
            }
          );
        }

        // F1 image: fades in (76–82%) — SAME window as the heading slide.
        if (heroF1Image) {
          window.gsap.to(heroF1Image, {
            scrollTrigger: {
              trigger: heroEl,
              start: '76% top',
              end: '82% top',
              scrub: 0.3
            },
            opacity: 1,
            ease: 'none'
          });
        }

        // Canvas (frame 32 lock): fades out as F1 image fades in (76–82%)
        if (heroCanvas) {
          window.gsap.to(heroCanvas, {
            scrollTrigger: {
              trigger: heroEl,
              start: '76% top',
              end: '82% top',
              scrub: 0.3
            },
            opacity: 0,
            ease: 'none'
          });
        }

        // F1 payoff ("On Purpose."): long call/response hold — after
        // the headline lands at 82%, holds alone for 10% before the
        // payoff and body cascade arrive as a burst.
        var heroF1Payoff = document.querySelector('[data-ss-hero-feature1-payoff]');
        if (heroF1Payoff) {
          window.gsap.fromTo(heroF1Payoff,
            { opacity: 0, y: 30 },
            {
              scrollTrigger: {
                trigger: heroEl,
                start: '92% top',
                end: '94% top',
                scrub: 0.3
              },
              opacity: 1,
              y: 0,
              ease: 'power2.out'
            }
          );
        }

        // F1 body lines: rapid cascade after the long hold.
        var heroF1Lines = document.querySelectorAll('[data-ss-hero-f1-line]');
        var lineTimings = [
          { start: 92, end: 94 },  // Line 1
          { start: 93, end: 95 },  // Line 2
          { start: 94, end: 96 },  // Line 3
          { start: 95, end: 97 }   // Line 4
        ];
        heroF1Lines.forEach(function (line, i) {
          var t = lineTimings[i];
          if (!t) return;
          window.gsap.fromTo(line,
            { opacity: 0, y: 28 },
            {
              scrollTrigger: {
                trigger: heroEl,
                start: t.start + '% top',
                end: t.end + '% top',
                scrub: 0.3
              },
              opacity: 1,
              y: 0,
              ease: 'power2.out'
            }
          );
        });

        // F1 kicker: closes the sequence with a stronger entrance (95–99%).
        // Slight scale-up + fade, longer window so it lands with weight.
        var heroF1Kicker = document.querySelector('[data-ss-hero-f1-kicker]');
        if (heroF1Kicker) {
          window.gsap.fromTo(heroF1Kicker,
            { opacity: 0, y: 30, scale: 0.96 },
            {
              scrollTrigger: {
                trigger: heroEl,
                start: '95% top',
                end: '99% top',
                scrub: 0.3
              },
              opacity: 1,
              y: 0,
              scale: 1,
              ease: 'power3.out'
            }
          );
          // Highlighter wipe — deep-brass band reveals left-to-right
          // behind the kicker text in the same scroll window.
          // Desktop only — on mobile the multi-line wrap breaks the
          // highlight visually, so we skip it (CSS also hides the
          // pseudo at ≤720px as a safety net).
          var heroF1Hl = heroF1Kicker.querySelector('[data-ss-hl]');
          if (heroF1Hl && !isMobile) {
            window.gsap.fromTo(heroF1Hl,
              { '--ss-hl-w': '0%' },
              {
                scrollTrigger: {
                  trigger: heroEl,
                  start: '95% top',
                  end: '99% top',
                  scrub: 0.3
                },
                '--ss-hl-w': '100%',
                ease: 'power2.inOut'
              }
            );
          }
        }

        } // end if (!isMobile) — close the F1 inside-hero gate

        // === Hard-hide hero pin once user scrolls past hero's bottom ===
        // Without this, Lenis smooth-scroll interpolates the visual scroll
        // position through the pin-release moment, making the hero F1 look
        // like it's sliding off — which violates the "images never translate"
        // rule. visibility:hidden snaps it out instantly. F2's carryover
        // (in F2 pin, z-index 5, identical content + position) takes over
        // at this exact scrollY, so the swap is silent.
        // DESKTOP ONLY — mobile hero isn't pinned, so nothing to hide.
        if (!isMobile) window.ScrollTrigger.create({
          trigger: heroEl,
          start: 'bottom top',
          onEnter: function () {
            if (heroPinEl) heroPinEl.style.visibility = 'hidden';
          },
          onLeaveBack: function () {
            if (heroPinEl) heroPinEl.style.visibility = 'visible';
          }
        });

        // Canvas-based frame scrub — DESKTOP ONLY. The mobile hero
        // controller below loads + plays the same frame sequence on a
        // time-based timeline (not scroll), so this scroll-scrubbed
        // version is gated off on mobile to avoid double work.
        if (heroCanvas && heroFrameUrls.length && !isMobile) {
          var heroCtx = heroCanvas.getContext('2d');
          var heroDpr = window.devicePixelRatio || 1;
          var heroFrameImgs = [];
          var heroLastIdx = -1;
          var heroCurrentProgress = 0;

          function sizeHeroCanvas() {
            var rect = heroCanvas.getBoundingClientRect();
            heroCanvas.width = Math.round(rect.width * heroDpr);
            heroCanvas.height = Math.round(rect.height * heroDpr);
          }

          function drawHeroFrame(idx) {
            var img = heroFrameImgs[idx];
            // If target frame not yet loaded, find the closest loaded frame
            if (!img || !img.complete || !img.naturalWidth) {
              for (var d = 1; d < heroFrameUrls.length; d++) {
                var below = heroFrameImgs[idx - d];
                if (below && below.complete && below.naturalWidth) { img = below; break; }
                var above = heroFrameImgs[idx + d];
                if (above && above.complete && above.naturalWidth) { img = above; break; }
              }
            }
            if (!img || !img.complete || !img.naturalWidth) return;
            var w = heroCanvas.width;
            var h = heroCanvas.height;
            heroCtx.fillStyle = '#000';
            heroCtx.fillRect(0, 0, w, h);
            // Both desktop and mobile use HEIGHT-COVER — the image fills
            // the viewport vertically and is centered horizontally (any
            // overflow gets cropped left/right). Desktop sits dead-center
            // (dy = 0). Mobile scoots up slightly so the hotspot lands in
            // the upper portion of the frame rather than the bottom.
            var imgRatio = img.naturalWidth / img.naturalHeight;
            var dw, dh, dx, dy;
            // Gate by viewport width, NOT by canvas/image aspect.
            // Hero frames are 16:9 (1.778) — most desktop viewports
            // (16:10 laptops, etc.) are narrower-aspect, so an
            // aspect-based gate misfired and sent desktop into the
            // mobile branch. CSS mobile breakpoint is 720px.
            var isMobileViewport = window.innerWidth <= 720;
            if (!isMobileViewport) {
              // Desktop — height-fit, dead center. Whole image
              // visible top-to-bottom; any extra horizontal canvas
              // sits as black bars on the sides (invisible on the
              // black page background).
              dh = h;
              dw = h * imgRatio;
              dx = (w - dw) / 2;
              dy = 0;
            } else {
              // Mobile — height-cover, scooted up so the hotspot
              // lands in the upper portion of the frame.
              dh = h;
              dw = h * imgRatio;
              dx = (w - dw) / 2;
              dy = -0.15 * h;
            }
            heroCtx.drawImage(img, dx, dy, dw, dh);
            heroLastIdx = idx;
          }

          // Preload all frames
          heroFrameUrls.forEach(function (url, i) {
            var img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = function () {
              if (i === 0) {
                sizeHeroCanvas();
                drawHeroFrame(0);
              }
              // If a recently-needed frame just landed, redraw current state
              var targetIdx = Math.min(heroFrameUrls.length - 1, Math.floor(heroCurrentProgress * (heroFrameUrls.length - 1)));
              if (i === targetIdx) drawHeroFrame(targetIdx);
            };
            img.src = url;
            heroFrameImgs[i] = img;
          });

          // Scroll drives frame index — scrub completes at 16% of section.
          // Hero is 700vh (so scrub runs across first ~112vh).
          // Remaining 84% drives bridge beats (with slow crossfades), pause, F1 composition.
          window.ScrollTrigger.create({
            trigger: heroEl,
            start: 'top top',
            end: '16% top',
            scrub: 0.3,
            onUpdate: function (self) {
              heroCurrentProgress = self.progress;
              var idx = Math.min(heroFrameUrls.length - 1, Math.floor(self.progress * (heroFrameUrls.length - 1)));
              if (idx !== heroLastIdx) drawHeroFrame(idx);
            }
          });

          // Resize handler — re-size canvas and redraw
          var heroResizeTimer;
          window.addEventListener('resize', function () {
            clearTimeout(heroResizeTimer);
            heroResizeTimer = setTimeout(function () {
              sizeHeroCanvas();
              var idx = Math.min(heroFrameUrls.length - 1, Math.floor(heroCurrentProgress * (heroFrameUrls.length - 1)));
              drawHeroFrame(idx);
            }, 150);
          });
        }
      } else if (heroOverlay) {
        // Reduced-motion: just show title, no scroll choreography
        heroOverlay.style.opacity = '1';
        // Draw the final frame so user sees the revealed product
        if (heroCanvas && heroFrameUrls.length) {
          var staticCtx = heroCanvas.getContext('2d');
          var staticImg = new Image();
          staticImg.crossOrigin = 'anonymous';
          staticImg.onload = function () {
            var rect = heroCanvas.getBoundingClientRect();
            heroCanvas.width = rect.width;
            heroCanvas.height = rect.height;
            staticCtx.fillStyle = '#000';
            staticCtx.fillRect(0, 0, heroCanvas.width, heroCanvas.height);
            var imgRatio = staticImg.naturalWidth / staticImg.naturalHeight;
            var dw = heroCanvas.width;
            var dh = dw / imgRatio;
            var dx = 0;
            var dy = (heroCanvas.height - dh) / 2;
            staticCtx.drawImage(staticImg, dx, dy, dw, dh);
          };
          staticImg.src = heroFrameUrls[heroFrameUrls.length - 1];
        }
      }

      // === STORY SCENE: per-beat entrance animations ===
      var storyBeats = document.querySelectorAll('.ss-launch-story__beat');
      var storyImgs = document.querySelectorAll('.ss-launch-story__img');
      if (storyBeats.length && !reduced) {
        var baseFilter = 'brightness(0.6) saturate(0.85)';
        var targetOpacity = 0.5;

        // Distinct entrance for each beat's image
        var beatAnimations = [
          // Beat 0 — "G10. Thick." — heavy zoom-down, mass settling
          {
            from: { opacity: 0, scale: 1.35, yPercent: -8, filter: 'blur(0px) ' + baseFilter },
            to: { opacity: targetOpacity, scale: 1, yPercent: 0, duration: 1.8, ease: 'power3.out' }
          },
          // Beat 1 — "Brass." — horizontal slide-in with parallax scale
          {
            from: { opacity: 0, scale: 1.14, xPercent: 8, filter: 'blur(0px) ' + baseFilter },
            to: { opacity: targetOpacity, scale: 1, xPercent: 0, duration: 1.5, ease: 'power2.out' }
          },
          // Beat 2 — "A sight you set yourself." — focus pull, blur to sharp
          {
            from: { opacity: 0, scale: 1.06, filter: 'blur(14px) ' + baseFilter },
            to: { opacity: targetOpacity, scale: 1, filter: 'blur(0px) ' + baseFilter, duration: 1.6, ease: 'power2.out' }
          }
        ];

        var activeIndex = -1;
        function activateStoryBeat(index) {
          if (index === activeIndex) return;
          activeIndex = index;
          storyImgs.forEach(function (img, i) {
            img.classList.toggle('is-active', i === index);
            if (i === index && beatAnimations[i]) {
              window.gsap.fromTo(img, beatAnimations[i].from, beatAnimations[i].to);
            } else {
              window.gsap.to(img, { opacity: 0, duration: 0.55, ease: 'power2.out' });
            }
          });
        }

        storyBeats.forEach(function (beat, i) {
          var heading = beat.querySelector('.ss-launch-story__heading');
          var copy = beat.querySelector('.ss-launch-story__copy');
          var els = [heading, copy].filter(Boolean);

          // Copy fade-in on enter
          if (els.length) {
            window.gsap.from(els, {
              scrollTrigger: {
                trigger: beat,
                start: 'top 75%',
                end: 'top 30%',
                scrub: 0.6
              },
              opacity: 0,
              y: 40,
              stagger: 0.12
            });
          }

          // Image swap when this beat dominates the viewport
          window.ScrollTrigger.create({
            trigger: beat,
            start: 'top 60%',
            end: 'bottom 40%',
            onEnter: function () { activateStoryBeat(i); },
            onEnterBack: function () { activateStoryBeat(i); }
          });
        });
      }

      // === STICKY BAR: slow opacity-only fade across the full F1 reveal ===
      // Starts the moment F1 headblock begins rising (80%) and finishes
      // around the kicker landing (99%). Long, slow, opacity-only — feels
      // like the bar materializes rather than drops in. No y translation.
      var stickyBar = document.querySelector('.ss-launch-stickybar');
      if (stickyBar && heroEl) {
        if (reduced) {
          stickyBar.style.opacity = '1';
        } else {
          window.gsap.set(stickyBar, { opacity: 0 });
          window.gsap.to(stickyBar, {
            scrollTrigger: {
              trigger: heroEl,
              start: '80% top',
              end: '99% top',
              scrub: 0.6
            },
            opacity: 1,
            ease: 'none'
          });
        }
      }

      // === STICKY BAR: solid-background toggle ===
      // On mobile the bar is transparent through the hero frames so it
      // floats over the canvas reveal. Once the CTA section ("Limited
      // Edition / Yours, if you want it") reaches the top of the
      // viewport, the page is static black content from here on — flip
      // the bar to a solid background so the type still reads cleanly
      // against everything below (gallery, story, tech specs, video,
      // closeout). CSS scopes the visual change to mobile; desktop
      // keeps its frosted glass throughout.
      var ctaSection = document.querySelector('.ss-launch-cta');
      if (stickyBar && ctaSection && !reduced) {
        window.ScrollTrigger.create({
          trigger: ctaSection,
          start: 'top top',
          onEnter: function () { stickyBar.classList.add('is-solid'); },
          onLeaveBack: function () { stickyBar.classList.remove('is-solid'); }
        });
      }

      // === BUY NOW: AJAX add-to-cart → straight to checkout ===
      // Single variant + clear intent = Apple-style "Buy Now" shortcut.
      // Falls back to a regular form POST if the AJAX call fails;
      // a hidden return_to=/checkout makes the fallback also land
      // on checkout (not /cart). A 2.5s safety timer always
      // re-enables the button so it can never get stuck on "Adding…".
      function attachBuyHandler(form, btn, label) {
        if (!form || !btn) return;
        // Ensure a return_to is present so form-fallback hits checkout.
        if (!form.querySelector('input[name="return_to"]')) {
          var ret = document.createElement('input');
          ret.type = 'hidden';
          ret.name = 'return_to';
          ret.value = '/checkout';
          form.appendChild(ret);
        }
        form.addEventListener('submit', function (e) {
          if (btn.hasAttribute('data-ss-soldout')) return;
          e.preventDefault();
          var originalLabel = label ? label.textContent : '';
          if (label) label.textContent = 'Adding…';
          btn.disabled = true;
          var safetyTimer = setTimeout(function () {
            // If navigation hasn't taken over in 2.5s, restore the
            // button so the user isn't stuck staring at "Adding…".
            if (label) label.textContent = originalLabel;
            btn.disabled = false;
          }, 2500);
          var fd = new FormData(form);
          fetch('/cart/add.js', {
            method: 'POST',
            body: fd,
            headers: { 'Accept': 'application/json' }
          }).then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          }).then(function () {
            window.location.href = '/checkout';
          }).catch(function () {
            clearTimeout(safetyTimer);
            if (label) label.textContent = originalLabel;
            btn.disabled = false;
            form.submit();
          });
        });
      }

      // Sticky bar Buy Now
      attachBuyHandler(
        document.querySelector('[data-ss-stickybar-form]'),
        document.querySelector('[data-ss-stickybar-btn]'),
        document.querySelector('[data-ss-stickybar-label]')
      );

      // After-F3 CTA Buy Now
      attachBuyHandler(
        document.querySelector('[data-ss-cta-form]'),
        document.querySelector('[data-ss-cta-btn]'),
        document.querySelector('[data-ss-cta-label]')
      );

      // Closeout Buy Now
      attachBuyHandler(
        document.querySelector('[data-ss-closeout-form]'),
        document.querySelector('[data-ss-closeout-btn]'),
        document.querySelector('[data-ss-closeout-label]')
      );

      // === SHOP PROMISE shadow-DOM style injection ===
      // Shopify's <delivery-promise-wc> is a custom element with a
      // real Shadow DOM, so regular CSS can't reach inside. We
      // append a <style> into its shadow root to strip the gray
      // box, center the content, and tint the text. We also widen
      // the host (Shopify sets it to 324px inline).
      (function shopPromiseStyle() {
        var SHADOW_CSS = '' +
          '[class*="ShopPromiseLayout__Clickable"],' +
          '[class*="ContainerCommon__Container"]{' +
            'background:transparent !important;' +
            'background-color:transparent !important;' +
            'padding:0 !important;' +
            'border:0 !important;' +
            'border-radius:0 !important;' +
            'box-shadow:none !important;' +
          '}' +
          '[class*="BlockStack__BlockStack"]{' +
            '--ui-block-stack-align:center !important;' +
            'align-items:center !important;' +
            'text-align:center !important;' +
          '}' +
          '[class*="InlineStack__InlineStack"]{' +
            '--ui-inline-stack-align:center !important;' +
            '--ui-inline-stack-block-align:center !important;' +
            'justify-content:center !important;' +
            'align-items:center !important;' +
          '}' +
          '[class*="Text__Text"],' +
          '[class*="ContainerCommon__FontOverride"]{' +
            'color:rgba(255,255,255,0.78) !important;' +
          '}';

        function styleHost(wc) {
          if (!wc || !wc.shadowRoot || wc.__ssStyled) return;
          wc.__ssStyled = true;
          var s = document.createElement('style');
          s.textContent = SHADOW_CSS;
          wc.shadowRoot.appendChild(s);
          // Widen the host past Shopify's inline 324px.
          wc.style.display = 'block';
          wc.style.width = '100%';
          wc.style.maxWidth = 'none';
          wc.style.margin = '1.25rem auto 0';
        }
        function styleAll() {
          document.querySelectorAll('delivery-promise-wc').forEach(styleHost);
        }
        styleAll();
        // Widget loads async — observe for late insertion.
        var mo = new MutationObserver(styleAll);
        mo.observe(document.body, { childList: true, subtree: true });
        if (window.customElements && window.customElements.whenDefined) {
          window.customElements.whenDefined('delivery-promise-wc').then(styleAll);
        }
      })();

      // === "WATCH IT AGAIN" — long, smooth scroll back to the title ===
      // Lenis-driven so it overrides any in-flight snap / momentum and
      // lands cleanly at scrollY = 0 ready for the next playthrough.
      var replayBtn = document.querySelector('[data-ss-replay]');
      if (replayBtn) {
        replayBtn.addEventListener('click', function () {
          if (window.__ssLenis && typeof window.__ssLenis.scrollTo === 'function') {
            window.__ssLenis.scrollTo(0, {
              duration: 2.6,
              easing: function (t) {
                return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
              },
              lock: true
            });
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      }

      // === LIGHTBOX (gallery photos + tech-specs dimensions) ===
      // Native pinch-zoom is disabled page-wide (it broke scrolling), so the
      // lightbox provides its OWN transform-based zoom: pinch to zoom, drag
      // to pan when zoomed, double-tap to reset. Gallery tiles open with
      // prev/next; the dimensions image opens as a single zoomable image.
      var lightbox     = document.querySelector('[data-ss-lightbox]');
      var lbImg        = document.querySelector('[data-ss-lightbox-img]');
      var lbCounter    = document.querySelector('[data-ss-lightbox-counter]');
      var lbClose      = document.querySelector('[data-ss-lightbox-close]');
      var lbPrev       = document.querySelector('[data-ss-lightbox-prev]');
      var lbNext       = document.querySelector('[data-ss-lightbox-next]');
      var galleryTiles = document.querySelectorAll('[data-ss-gallery-tile]');
      var dimsTrigger  = document.querySelector('[data-ss-zoom-tile]');

      if (lightbox && lbImg) {
        var tileImgs = Array.prototype.map.call(galleryTiles, function (t) {
          var img = t.querySelector('img');
          return img ? img.getAttribute('src') : '';
        });
        var current = 0;
        var lastFocus = null;
        var galleryMode = true;

        // ---- transform-based zoom/pan ----
        var zScale = 1, zTx = 0, zTy = 0;
        function zApply() { lbImg.style.transform = 'translate(' + zTx + 'px,' + zTy + 'px) scale(' + zScale + ')'; }
        function zReset(animate) {
          zScale = 1; zTx = 0; zTy = 0;
          lbImg.style.transition = animate ? 'transform 0.2s ease' : 'none';
          zApply();
        }
        function zClamp() {
          var maxX = Math.max(0, (lbImg.offsetWidth  * zScale - window.innerWidth)  / 2);
          var maxY = Math.max(0, (lbImg.offsetHeight * zScale - window.innerHeight) / 2);
          zTx = Math.max(-maxX, Math.min(maxX, zTx));
          zTy = Math.max(-maxY, Math.min(maxY, zTy));
        }

        function showControls(show) {
          if (lbPrev)    lbPrev.style.display    = show ? '' : 'none';
          if (lbNext)    lbNext.style.display    = show ? '' : 'none';
          if (lbCounter) lbCounter.style.display = show ? '' : 'none';
        }

        function openCommon() {
          lastFocus = document.activeElement;
          lightbox.hidden = false;
          lightbox.setAttribute('aria-hidden', 'false');
          requestAnimationFrame(function () { lightbox.classList.add('is-open'); });
          document.body.style.overflow = 'hidden';
          zReset(false);
          if (lbClose) lbClose.focus();
        }

        function openLb(idx) {
          galleryMode = true;
          showControls(true);
          current = idx;
          lbImg.src = tileImgs[current];
          lbImg.alt = 'Slant Spartan photo ' + (current + 1);
          if (lbCounter) lbCounter.textContent = (current + 1) + ' / ' + tileImgs.length;
          openCommon();
        }

        function openSingle(src, alt) {
          galleryMode = false;
          showControls(false);
          lbImg.src = src;
          lbImg.alt = alt || '';
          openCommon();
        }

        function closeLb() {
          lightbox.classList.remove('is-open');
          lightbox.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
          setTimeout(function () { lightbox.hidden = true; zReset(false); }, 220);
          if (lastFocus && typeof lastFocus.focus === 'function') {
            try { lastFocus.focus(); } catch (e) {}
          }
        }

        function step(delta) {
          if (!galleryMode || !tileImgs.length) return;
          var n = tileImgs.length;
          current = ((current + delta) % n + n) % n;
          lbImg.src = tileImgs[current];
          lbImg.alt = 'Slant Spartan photo ' + (current + 1);
          if (lbCounter) lbCounter.textContent = (current + 1) + ' / ' + tileImgs.length;
          zReset(true);
        }

        galleryTiles.forEach(function (tile) {
          tile.addEventListener('click', function () {
            openLb(parseInt(tile.getAttribute('data-idx'), 10) || 0);
          });
        });
        if (dimsTrigger) {
          var openDims = function () {
            var img = dimsTrigger.querySelector('img') || dimsTrigger;
            openSingle(img.getAttribute('src'), img.getAttribute('alt'));
          };
          dimsTrigger.addEventListener('click', openDims);
          dimsTrigger.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDims(); }
          });
        }

        if (lbClose) lbClose.addEventListener('click', closeLb);
        if (lbPrev)  lbPrev .addEventListener('click', function () { step(-1); });
        if (lbNext)  lbNext .addEventListener('click', function () { step( 1); });

        // Click outside the image (on the backdrop) closes
        lightbox.addEventListener('click', function (e) {
          if (e.target === lightbox) closeLb();
        });

        document.addEventListener('keydown', function (e) {
          if (lightbox.hidden) return;
          if (e.key === 'Escape') { closeLb(); }
          else if (galleryMode && e.key === 'ArrowLeft') { step(-1); }
          else if (galleryMode && e.key === 'ArrowRight') { step(1); }
        });

        // ---- touch: pinch-zoom + pan + double-tap reset ----
        var tMode = null, tStartDist = 0, tStartScale = 1, tStartTx = 0, tStartTy = 0, tLastX = 0, tLastY = 0, tLastTap = 0;
        function tDist(tt) { var dx = tt[0].clientX - tt[1].clientX, dy = tt[0].clientY - tt[1].clientY; return Math.sqrt(dx * dx + dy * dy); }

        lightbox.addEventListener('touchstart', function (e) {
          lbImg.style.transition = 'none';
          if (e.touches.length === 2) {
            tMode = 'pinch'; tStartDist = tDist(e.touches); tStartScale = zScale;
          } else if (e.touches.length === 1) {
            tMode = 'pan';
            tLastX = e.touches[0].clientX; tLastY = e.touches[0].clientY;
            tStartTx = zTx; tStartTy = zTy;
            var now = Date.now();
            if (now - tLastTap < 300) {
              lbImg.style.transition = 'transform 0.2s ease';
              if (zScale > 1) { zReset(true); } else { zScale = 2.5; zClamp(); zApply(); }
              tMode = 'dtap';
            }
            tLastTap = now;
          }
        }, { passive: true });

        lightbox.addEventListener('touchmove', function (e) {
          if (tMode === 'pinch' || (tMode === 'pan' && zScale > 1)) e.preventDefault();
          if (tMode === 'pinch' && e.touches.length === 2) {
            zScale = Math.max(1, Math.min(5, tStartScale * (tDist(e.touches) / tStartDist)));
            if (zScale === 1) { zTx = 0; zTy = 0; }
            zClamp(); zApply();
          } else if (tMode === 'pan' && e.touches.length === 1 && zScale > 1) {
            zTx = tStartTx + (e.touches[0].clientX - tLastX);
            zTy = tStartTy + (e.touches[0].clientY - tLastY);
            zClamp(); zApply();
          }
        }, { passive: false });

        lightbox.addEventListener('touchend', function (e) {
          // SWIPE to change gallery image — only when NOT zoomed (scale 1)
          // and it was a single-finger horizontal pan that didn't pan
          // (because there's nothing to pan at scale 1).
          if (tMode === 'pan' && zScale === 1 && galleryMode && e.changedTouches && e.changedTouches.length) {
            var dx = e.changedTouches[0].clientX - tLastX;
            var dy = e.changedTouches[0].clientY - tLastY;
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
              step(dx < 0 ? 1 : -1);
            }
          }
          if (e.touches.length === 0) { tMode = null; }
          else if (e.touches.length === 1) {
            tMode = 'pan';
            tLastX = e.touches[0].clientX; tLastY = e.touches[0].clientY;
            tStartTx = zTx; tStartTy = zTy;
          }
        }, { passive: true });
      }

      // === CTA: staged reveal as the section enters viewport ===
      // Eyebrow → heading → price → button → fine print, each rising
      // and fading. Scrub-tied so it tracks the user's scroll.
      var ctaEl       = document.querySelector('.ss-launch-cta');
      var ctaEyebrow  = document.querySelector('[data-ss-cta-eyebrow]');
      var ctaHeading  = document.querySelector('[data-ss-cta-heading]');
      var ctaSub      = document.querySelector('[data-ss-cta-sub]');
      var ctaPrice    = document.querySelector('[data-ss-cta-price]');
      var ctaForm     = document.querySelector('[data-ss-cta-form]');
      var ctaFine     = document.querySelector('[data-ss-cta-fine]');
      if (ctaEl && !reduced) {
        var ctaParts = [
          { el: ctaEyebrow, y: 20, startPct: 95, endPct: 80 },
          { el: ctaHeading, y: 32, startPct: 90, endPct: 70 },
          { el: ctaSub,     y: 24, startPct: 87, endPct: 67 },
          { el: ctaPrice,   y: 24, startPct: 85, endPct: 65 },
          { el: ctaForm,    y: 24, startPct: 80, endPct: 60 },
          { el: ctaFine,    y: 16, startPct: 75, endPct: 55 }
        ];
        ctaParts.forEach(function (p) {
          if (!p.el) return;
          window.gsap.fromTo(p.el,
            { opacity: 0, y: p.y },
            {
              scrollTrigger: {
                trigger: ctaEl,
                start: 'top ' + p.startPct + '%',
                end: 'top ' + p.endPct + '%',
                scrub: 0.4
              },
              opacity: 1,
              y: 0,
              ease: 'power2.out'
            }
          );
        });
      }

      // ╔══════════════════════════════════════════════════════════╗
      // ║  F1 → F2 TRANSITION — LOCKED PATTERN.                    ║
      // ║  This is the template for every feature hand-off.        ║
      // ╠══════════════════════════════════════════════════════════╣
      // ║  F2 is 400vh tall, pin scrollY 700–1100vh.               ║
      // ║                                                          ║
      // ║  Hero pin releases at scrollY=700vh. At the same moment, ║
      // ║  F2 pin engages with the F1 CARRYOVER already visible in ║
      // ║  identical position — silent visual hand-off.            ║
      // ║                                                          ║
      // ║   F2  0–37%   HOLD on the landed F1 kicker (~148vh of    ║
      // ║                stillness — longer pause Jacob asked for) ║
      // ║   F2 37–55%   TRANSITION (all four animations share one  ║
      // ║                  scrub + matched power1.inOut easing so  ║
      // ║                  F1 EXIT and F2 ENTRY are truly          ║
      // ║                  concurrent — same speed curve)          ║
      // ║                 • carryover image opacity 1 → 0  (fade)  ║
      // ║                 • F2 image opacity 0 → 1         (fade)  ║
      // ║                      (images crossfade in place,         ║
      // ║                       NEVER translate)                   ║
      // ║                 • carryover text container y: 0 → -100vh ║
      // ║                      (text slides off as a block)        ║
      // ║                 • F2 headblock rises in                  ║
      // ║   F2 55–65%   F2 body lines cascade (4 lines, 3% overlap)║
      // ║   F2 65–70%   F2 kicker lands with weight                ║
      // ║   F2 70–100%  HOLD on revealed F2                        ║
      // ╚══════════════════════════════════════════════════════════╝
      var f2El = document.querySelector('.ss-launch-feature2');
      var f2Pin = document.querySelector('.ss-launch-feature2__pin');
      var f1CarryImage = document.querySelector('[data-ss-f1-carryover-image]');
      var f1CarryText = document.querySelector('[data-ss-f1-carryover-text]');
      var f2Image = document.querySelector('[data-ss-feature2-image]');
      var f2Headblock = document.querySelector('[data-ss-feature2-headblock]');
      var f2Lines = document.querySelectorAll('[data-ss-feature2-line]');
      var f2Kicker = document.querySelector('[data-ss-feature2-kicker]');

      // F2/F3/F4 setup is desktop-only — mobile uses normal-flow
      // scrolling via CSS overrides (no pin, no scrub, no animation).
      if (f2El && f2Pin && !reduced && !isMobile) {
        // F2 pin engages at scrollY=700vh (exactly when hero pin
        // releases). pinSpacing:false keeps DOM flow tight.
        // visibility:hidden default in CSS; onEnter flips it visible
        // — same portable pattern as F3 so we don't depend on
        // z-index ordering for clean hand-offs.
        // Mobile uses the same pin/animation architecture as desktop,
        // just reflowed via CSS to stacked (image top / text bottom).
        window.ScrollTrigger.create({
          trigger: f2El,
          start: 'top top',
          end: 'bottom top',
          pin: f2Pin,
          pinSpacing: false,
          onEnter: function () { f2Pin.style.visibility = 'visible'; },
          onLeaveBack: function () { f2Pin.style.visibility = 'hidden'; }
        });

        // === Shared transition window: F2 38% → 56% ===
        // F2 hold extended specifically to compensate for the fact
        // that F1's kicker lands at 99% of hero — almost no post-
        // kicker hold in the hero pin itself. By holding F2 longer
        // BEFORE its transition, the F1→F2 gap roughly matches the
        // F2→F3 and F3→F4 gaps. F3 and F4 stay at their 18% hold.
        var transitionTrigger = {
          trigger: f2El,
          start: '38% top',
          end: '56% top',
          scrub: 0.4
        };

        // F1 carryover image: opacity-only fade out. Stays put.
        if (f1CarryImage) {
          window.gsap.to(f1CarryImage, {
            scrollTrigger: transitionTrigger,
            opacity: 0,
            ease: 'none'
          });
        }

        // F2 image: opacity-only fade in. Stays put.
        if (f2Image) {
          window.gsap.to(f2Image, {
            scrollTrigger: transitionTrigger,
            opacity: 1,
            ease: 'none'
          });
        }

        // F1 carryover text container: whole block slides OFF upward.
        // ONLY the text container translates — the image never moves.
        // Matched power1.inOut easing with the F2 headblock so they
        // move in lockstep.
        if (f1CarryText) {
          window.gsap.fromTo(f1CarryText,
            { y: 0 },
            {
              scrollTrigger: transitionTrigger,
              y: '-100vh',
              ease: 'power1.inOut'
            }
          );
        }

        // F2 headblock: rises into right-column center. Matched
        // power1.inOut easing with the F1 carryover slide so the two
        // movements feel like a single coordinated push.
        if (f2Headblock) {
          window.gsap.fromTo(f2Headblock,
            { opacity: 0, y: '55vh' },
            {
              scrollTrigger: transitionTrigger,
              opacity: 1,
              y: 0,
              ease: 'power1.inOut'
            }
          );
        }

        // F2 payoff ("You Stay Consistent.") — long call/response hold.
        // Transition ends at 56%, payoff lands at 69 → 13% headline-
        // alone hold for a clear reading beat.
        var f2Payoff = document.querySelector('[data-ss-feature2-payoff]');
        if (f2Payoff) {
          window.gsap.fromTo(f2Payoff,
            { opacity: 0, y: 30 },
            {
              scrollTrigger: { trigger: f2El, start: '69% top', end: '72% top', scrub: 0.3 },
              opacity: 1,
              y: 0,
              ease: 'power2.out'
            }
          );
        }

        // === F2 body lines cascade === (shifted to land after the hold)
        var f2LineTimings = [
          { start: 69, end: 72 },
          { start: 71, end: 74 },
          { start: 73, end: 76 },
          { start: 75, end: 78 }
        ];
        f2Lines.forEach(function (line, i) {
          var t = f2LineTimings[i];
          if (!t) return;
          window.gsap.fromTo(line,
            { opacity: 0, y: 28 },
            {
              scrollTrigger: {
                trigger: f2El,
                start: t.start + '% top',
                end: t.end + '% top',
                scrub: 0.3
              },
              opacity: 1,
              y: 0,
              ease: 'power2.out'
            }
          );
        });

        // === F2 kicker — lands with weight ===
        if (f2Kicker) {
          window.gsap.fromTo(f2Kicker,
            { opacity: 0, y: 30, scale: 0.96 },
            {
              scrollTrigger: {
                trigger: f2El,
                start: '77% top',
                end: '82% top',
                scrub: 0.3
              },
              opacity: 1,
              y: 0,
              scale: 1,
              ease: 'power3.out'
            }
          );
          var f2Hl = f2Kicker.querySelector('[data-ss-hl]');
          if (f2Hl && !isMobile) {
            window.gsap.fromTo(f2Hl,
              { '--ss-hl-w': '0%' },
              {
                scrollTrigger: { trigger: f2El, start: '77% top', end: '82% top', scrub: 0.3 },
                '--ss-hl-w': '100%',
                ease: 'power2.inOut'
              }
            );
          }
        }
      }

      // ╔══════════════════════════════════════════════════════════╗
      // ║  F2 → F3 TRANSITION — same locked pattern as F1→F2.      ║
      // ╠══════════════════════════════════════════════════════════╣
      // ║  F3 is 400vh tall, pin scrollY 1100–1500vh.              ║
      // ║                                                          ║
      // ║  F2 pin's visibility hides at scrollY=1100vh (mirroring  ║
      // ║  hero pin's hide). At the same moment F3 pin engages     ║
      // ║  with the F2 CARRYOVER already visible in identical      ║
      // ║  position — silent visual hand-off.                      ║
      // ║                                                          ║
      // ║   F3  0–37%   HOLD on the landed F2 kicker (~148vh)      ║
      // ║   F3 37–55%   TRANSITION                                 ║
      // ║                 • carryover image opacity 1 → 0          ║
      // ║                 • F3 image opacity 0 → 1                 ║
      // ║                 • carryover text container y: 0 → -100vh ║
      // ║                 • F3 headblock rises in                  ║
      // ║   F3 55–65%   F3 body lines cascade                      ║
      // ║   F3 65–70%   F3 kicker lands                            ║
      // ║   F3 70–100%  HOLD on revealed F3                        ║
      // ╚══════════════════════════════════════════════════════════╝
      var f3El = document.querySelector('.ss-launch-feature3');
      var f3Pin = document.querySelector('.ss-launch-feature3__pin');
      var f2CarryImage = document.querySelector('[data-ss-f2-carryover-image]');
      var f2CarryText = document.querySelector('[data-ss-f2-carryover-text]');
      var f3Image = document.querySelector('[data-ss-feature3-image]');
      var f3Headblock = document.querySelector('[data-ss-feature3-headblock]');
      var f3Lines = document.querySelectorAll('[data-ss-feature3-line]');
      var f3Kicker = document.querySelector('[data-ss-feature3-kicker]');

      if (f3El && f3Pin && !reduced && !isMobile) {
        // Pin + flip visibility on engage. F3 pin defaults to
        // visibility:hidden in CSS so its contents don't peek from
        // below during the approach. onEnter reveals it the instant
        // the pin engages; onLeaveBack hides it again when scrolling
        // back into F2.
        window.ScrollTrigger.create({
          trigger: f3El,
          start: 'top top',
          end: 'bottom top',
          pin: f3Pin,
          pinSpacing: false,
          onEnter: function () { f3Pin.style.visibility = 'visible'; },
          onLeaveBack: function () { f3Pin.style.visibility = 'hidden'; }
        });

        var f3TransitionTrigger = {
          trigger: f3El,
          start: '18% top',
          end: '36% top',
          scrub: 0.4
        };

        if (f2CarryImage) {
          window.gsap.to(f2CarryImage, {
            scrollTrigger: f3TransitionTrigger,
            opacity: 0,
            ease: 'none'
          });
        }

        if (f3Image) {
          window.gsap.to(f3Image, {
            scrollTrigger: f3TransitionTrigger,
            opacity: 1,
            ease: 'none'
          });
        }

        if (f2CarryText) {
          window.gsap.fromTo(f2CarryText,
            { y: 0 },
            {
              scrollTrigger: f3TransitionTrigger,
              y: '-100vh',
              ease: 'power1.inOut'
            }
          );
        }

        if (f3Headblock) {
          window.gsap.fromTo(f3Headblock,
            { opacity: 0, y: '55vh' },
            {
              scrollTrigger: f3TransitionTrigger,
              opacity: 1,
              y: 0,
              ease: 'power1.inOut'
            }
          );
        }

        // F3 payoff ("Lots of Brass.") — 13% hold after transition lands.
        var f3Payoff = document.querySelector('[data-ss-feature3-payoff]');
        if (f3Payoff) {
          window.gsap.fromTo(f3Payoff,
            { opacity: 0, y: 30 },
            {
              scrollTrigger: { trigger: f3El, start: '49% top', end: '52% top', scrub: 0.3 },
              opacity: 1,
              y: 0,
              ease: 'power2.out'
            }
          );
        }

        var f3LineTimings = [
          { start: 49, end: 52 },
          { start: 51, end: 54 },
          { start: 53, end: 56 },
          { start: 55, end: 58 }
        ];
        f3Lines.forEach(function (line, i) {
          var t = f3LineTimings[i];
          if (!t) return;
          window.gsap.fromTo(line,
            { opacity: 0, y: 28 },
            {
              scrollTrigger: {
                trigger: f3El,
                start: t.start + '% top',
                end: t.end + '% top',
                scrub: 0.3
              },
              opacity: 1,
              y: 0,
              ease: 'power2.out'
            }
          );
        });

        if (f3Kicker) {
          window.gsap.fromTo(f3Kicker,
            { opacity: 0, y: 30, scale: 0.96 },
            {
              scrollTrigger: {
                trigger: f3El,
                start: '57% top',
                end: '62% top',
                scrub: 0.3
              },
              opacity: 1,
              y: 0,
              scale: 1,
              ease: 'power3.out'
            }
          );
          var f3Hl = f3Kicker.querySelector('[data-ss-hl]');
          if (f3Hl && !isMobile) {
            window.gsap.fromTo(f3Hl,
              { '--ss-hl-w': '0%' },
              {
                scrollTrigger: { trigger: f3El, start: '57% top', end: '62% top', scrub: 0.3 },
                '--ss-hl-w': '100%',
                ease: 'power2.inOut'
              }
            );
          }
        }
      }

      // ╔══════════════════════════════════════════════════════════╗
      // ║  F3 → F4 TRANSITION — same locked pattern as F2→F3.      ║
      // ╠══════════════════════════════════════════════════════════╣
      // ║  F4 is 400vh tall, pin scrollY 1500–1900vh.              ║
      // ║  F3 pin visibility hides at scrollY=1500vh; F4 pin       ║
      // ║  engages at the same moment with the F3 CARRYOVER        ║
      // ║  already visible — silent hand-off.                      ║
      // ╚══════════════════════════════════════════════════════════╝
      var f4El = document.querySelector('.ss-launch-feature4');
      var f4Pin = document.querySelector('.ss-launch-feature4__pin');
      var f3CarryImage = document.querySelector('[data-ss-f3-carryover-image]');
      var f3CarryText = document.querySelector('[data-ss-f3-carryover-text]');
      var f4Image = document.querySelector('[data-ss-feature4-image]');
      var f4Headblock = document.querySelector('[data-ss-feature4-headblock]');
      var f4Lines = document.querySelectorAll('[data-ss-feature4-line]');
      var f4Kicker = document.querySelector('[data-ss-feature4-kicker]');

      if (f4El && f4Pin && !reduced && !isMobile) {
        window.ScrollTrigger.create({
          trigger: f4El,
          start: 'top top',
          end: 'bottom top',
          pin: f4Pin,
          pinSpacing: false,
          onEnter: function () { f4Pin.style.visibility = 'visible'; },
          onLeaveBack: function () { f4Pin.style.visibility = 'hidden'; }
        });

        var f4TransitionTrigger = {
          trigger: f4El,
          start: '13% top',
          end: '31% top',
          scrub: 0.4
        };

        if (f3CarryImage) {
          window.gsap.to(f3CarryImage, {
            scrollTrigger: f4TransitionTrigger,
            opacity: 0,
            ease: 'none'
          });
        }
        if (f4Image) {
          window.gsap.to(f4Image, {
            scrollTrigger: f4TransitionTrigger,
            opacity: 1,
            ease: 'none'
          });
        }
        if (f3CarryText) {
          window.gsap.fromTo(f3CarryText,
            { y: 0 },
            { scrollTrigger: f4TransitionTrigger, y: '-100vh', ease: 'power1.inOut' }
          );
        }
        if (f4Headblock) {
          window.gsap.fromTo(f4Headblock,
            { opacity: 0, y: '55vh' },
            { scrollTrigger: f4TransitionTrigger, opacity: 1, y: 0, ease: 'power1.inOut' }
          );
        }

        // F4 payoff ("Yours Has a Number.") — 13% hold after transition lands.
        var f4Payoff = document.querySelector('[data-ss-feature4-payoff]');
        if (f4Payoff) {
          window.gsap.fromTo(f4Payoff,
            { opacity: 0, y: 30 },
            {
              scrollTrigger: { trigger: f4El, start: '44% top', end: '47% top', scrub: 0.3 },
              opacity: 1,
              y: 0,
              ease: 'power2.out'
            }
          );
        }

        var f4LineTimings = [
          { start: 44, end: 47 },
          { start: 46, end: 49 },
          { start: 48, end: 51 },
          { start: 50, end: 53 }
        ];
        f4Lines.forEach(function (line, i) {
          var t = f4LineTimings[i];
          if (!t) return;
          window.gsap.fromTo(line,
            { opacity: 0, y: 28 },
            {
              scrollTrigger: { trigger: f4El, start: t.start + '% top', end: t.end + '% top', scrub: 0.3 },
              opacity: 1, y: 0, ease: 'power2.out'
            }
          );
        });

        if (f4Kicker) {
          window.gsap.fromTo(f4Kicker,
            { opacity: 0, y: 30, scale: 0.96 },
            {
              scrollTrigger: { trigger: f4El, start: '55% top', end: '60% top', scrub: 0.3 },
              opacity: 1, y: 0, scale: 1, ease: 'power3.out'
            }
          );
          var f4Hl = f4Kicker.querySelector('[data-ss-hl]');
          if (f4Hl && !isMobile) {
            window.gsap.fromTo(f4Hl,
              { '--ss-hl-w': '0%' },
              {
                scrollTrigger: { trigger: f4El, start: '55% top', end: '60% top', scrub: 0.3 },
                '--ss-hl-w': '100%',
                ease: 'power2.inOut'
              }
            );
          }
        }
      }

      // === Hard-hide F2 pin once user scrolls past F2's bottom ===
      // Mirrors hero pin hide. Prevents F2 from appearing to scroll off
      // during F2→F3 hand-off. F3's carryover takes over visually.
      var f2PinEl = document.querySelector('.ss-launch-feature2__pin');
      if (f2El && f2PinEl && !reduced && !isMobile) {
        window.ScrollTrigger.create({
          trigger: f2El,
          start: 'bottom top',
          onEnter: function () {
            f2PinEl.style.visibility = 'hidden';
          },
          onLeaveBack: function () {
            f2PinEl.style.visibility = 'visible';
          }
        });
      }

      // === Hard-hide F3 pin once user scrolls past F3's bottom ===
      // Same pattern as F2 hide. F4's carryover takes over visually.
      var f3PinEl = document.querySelector('.ss-launch-feature3__pin');
      if (f3El && f3PinEl && !reduced && !isMobile) {
        window.ScrollTrigger.create({
          trigger: f3El,
          start: 'bottom top',
          onEnter: function () {
            f3PinEl.style.visibility = 'hidden';
          },
          onLeaveBack: function () {
            f3PinEl.style.visibility = 'visible';
          }
        });
      }

      // ╔══════════════════════════════════════════════════════════╗
      // ║  MOBILE HERO — GESTURE-STEPPED SEQUENCE (scroll-locked)  ║
      // ╠══════════════════════════════════════════════════════════╣
      // ║  Mobile-only (<=720px). The hero is fully decoupled from ║
      // ║  native scroll. While the user is in the hero, scrolling ║
      // ║  is LOCKED and each swipe advances exactly ONE state — no ║
      // ║  matter how fast, slow, or short the flick is. The        ║
      // ║  direction wins, never the distance. Transitions are     ║
      // ║  TIME-driven via GSAP timelines, so they play the same   ║
      // ║  way every time and never fight iOS momentum.            ║
      // ║                                                          ║
      // ║   State 0 : SLANT SPARTAN title                          ║
      // ║   0 -> 1  : title fades out, the CANVAS REVEAL plays     ║
      // ║             slowly (the background animation), then      ║
      // ║             bridge 1 fades in                            ║
      // ║   State 1 : "Ten years of refinement."                  ║
      // ║   State 2 : "One design."                               ║
      // ║   State 3 : "Only 320 made."                            ║
      // ║   State 4 : "Each one serialized."  <- holds here        ║
      // ║   A forward swipe at state 4 releases the scroll lock;   ║
      // ║   the rest of the site then scrolls natively.           ║
      // ║                                                          ║
      // ║  Timing tunables live in the MHERO object below.         ║
      // ╚══════════════════════════════════════════════════════════╝
      if (heroEl && isMobile && !reduced && window.gsap) {
        // ---- Tunable timing (seconds, except swipePx) ----
        var MHERO = {
          titleFadeOut : 0.55,  // title + designer fade as state 0 leaves
          canvasReveal : 2.40,  // SLOW background reveal on 0 -> 1 (the "awesome animation")
          canvasRewind : 1.20,  // reverse reveal on 1 -> 0
          bridgeIn     : 0.70,  // incoming bridge rise + fade
          bridgeOut    : 0.50,  // outgoing bridge fade
          swipePx      : 8      // min finger travel (px) to count a swipe (filters taps)
        };

        var mhOverlay = heroOverlay;                                  // title + designer line
        var mhBeats   = Array.prototype.slice.call(heroBridgeBeats);  // the 4 bridge beats
        var mhCanvas  = heroCanvas;
        var mhCtx     = mhCanvas ? mhCanvas.getContext('2d') : null;
        var mhDpr     = Math.min(window.devicePixelRatio || 1, 2);  // cap canvas backing-store memory
        var mhImgs    = [];
        var mhLastIdx = -1;
        // MEMORY: decoding all 32 full-res frames crashed iOS WebKit ("a
        // problem repeatedly occurred") under hard pinch-zoom. Use ~half the
        // frames and request smaller versions from the Shopify CDN. The hero
        // reveal is a dark lighting fade, so the lower res isn't noticeable.
        // Keep ALL frames for smoothness, but request smaller pixel
        // dimensions from the CDN so total decode memory stays safe.
        // (Decode RAM = width*height*4 regardless of JPEG quality, so the
        // pixel size — not compression — is what prevents the iOS crash.)
        // Prefer the purpose-built VERTICAL frames on mobile (portrait-native,
        // so every decoded pixel is on-screen). Served at width=720 to keep
        // decode RAM safe. Falls back to the desktop frames at width=1080.
        var mhBase = (heroFrameUrlsMobile && heroFrameUrlsMobile.length) ? heroFrameUrlsMobile : heroFrameUrls;
        var mhW = (heroFrameUrlsMobile && heroFrameUrlsMobile.length) ? 720 : 1080;
        function mhSized(u) { return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'width=' + mhW; }
        var mhUrls = mhBase.map(mhSized);

        function mhSize() {
          if (!mhCanvas) return;
          var r = mhCanvas.getBoundingClientRect();
          mhCanvas.width  = Math.round(r.width  * mhDpr);
          mhCanvas.height = Math.round(r.height * mhDpr);
        }
        function mhDraw(idx) {
          if (!mhCtx) return;
          var img = mhImgs[idx];
          if (!img || !img.complete || !img.naturalWidth) {
            for (var d = 1; d < mhUrls.length; d++) {
              var lo = mhImgs[idx - d];
              if (lo && lo.complete && lo.naturalWidth) { img = lo; break; }
              var hi = mhImgs[idx + d];
              if (hi && hi.complete && hi.naturalWidth) { img = hi; break; }
            }
          }
          if (!img || !img.complete || !img.naturalWidth) return;
          var w = mhCanvas.width, h = mhCanvas.height;
          mhCtx.fillStyle = '#000';
          mhCtx.fillRect(0, 0, w, h);
          // Mobile branch: height-cover, scooted up so the hotspot lands in
          // the upper portion of the frame (matches the desktop mobile path).
          var ratio = img.naturalWidth / img.naturalHeight;
          var dh = h, dw = h * ratio;
          var dx = (w - dw) / 2, dy = -0.15 * h;
          mhCtx.drawImage(img, dx, dy, dw, dh);
          mhLastIdx = idx;
        }
        function mhDrawProgress(p) {
          if (!mhUrls.length) return;
          var idx = Math.floor(p * (mhUrls.length - 1));
          if (idx < 0) idx = 0;
          if (idx > mhUrls.length - 1) idx = mhUrls.length - 1;
          if (idx !== mhLastIdx) mhDraw(idx);
        }

        // Preload the reduced/resized reveal frames (mobile owns its own copy;
        // the desktop scroll-scrub block is gated off on mobile).
        mhUrls.forEach(function (url, i) {
          var img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = function () {
            if (i === 0) { mhSize(); mhDraw(0); }
            else if (i === mhLastIdx) mhDraw(mhLastIdx);
          };
          img.src = url;
          mhImgs[i] = img;
        });

        // Redraw on viewport changes (mobile URL bar show/hide fires resize).
        var mhResizeTimer;
        window.addEventListener('resize', function () {
          clearTimeout(mhResizeTimer);
          mhResizeTimer = setTimeout(function () {
            mhSize();
            mhDraw(mhLastIdx < 0 ? 0 : mhLastIdx);
          }, 150);
        });

        // ---- Initial state: title up, bridges hidden, canvas at frame 0 ----
        if (mhCanvas) window.gsap.set(mhCanvas, { opacity: 1 });
        mhBeats.forEach(function (b) { window.gsap.set(b, { opacity: 0, y: 0 }); });

        var mhStep      = 0;     // current state 0..4
        var MH_LAST     = 4;     // state 4 = bridge 4 (the hold point)
        var mhAnimating = false;
        var mhReleased  = false;
        var mhFP        = { v: 0 };  // canvas frame-progress proxy for tweens

        // state n (1..4) maps to bridge element mhBeats[n-1]
        function mhBeatFor(state) { return mhBeats[state - 1] || null; }

        function mhGo(to) {
          if (mhAnimating || to < 0 || to > MH_LAST || to === mhStep) return;
          var from = mhStep;
          var fwd  = to > from;
          mhAnimating = true;
          var settled = false;
          function mhDone() { if (settled) return; settled = true; mhAnimating = false; mhStep = to; }
          var tl = window.gsap.timeline({ onComplete: mhDone });
          // Safety net: if onComplete somehow never fires, never leave the
          // page locked — clear the animating flag after a generous window.
          setTimeout(mhDone, 5000);

          if (from === 0 && to === 1) {
            // TITLE -> BRIDGE 1, with the slow canvas reveal between them.
            if (mhOverlay) tl.to(mhOverlay,
              { opacity: 0, y: -40, duration: MHERO.titleFadeOut, ease: 'power2.in' }, 0);
            tl.to(mhFP, { v: 1, duration: MHERO.canvasReveal, ease: 'none',
              onUpdate: function () { mhDrawProgress(mhFP.v); } }, 0.15);
            tl.fromTo(mhBeatFor(1), { opacity: 0, y: 40 },
              { opacity: 1, y: 0, duration: MHERO.bridgeIn, ease: 'power2.out' }, '>-0.15');
          } else if (from === 1 && to === 0) {
            // BRIDGE 1 -> TITLE, reverse the reveal.
            tl.to(mhBeatFor(1),
              { opacity: 0, y: 40, duration: MHERO.bridgeOut, ease: 'power2.in' }, 0);
            tl.to(mhFP, { v: 0, duration: MHERO.canvasRewind, ease: 'none',
              onUpdate: function () { mhDrawProgress(mhFP.v); } }, 0);
            if (mhOverlay) tl.fromTo(mhOverlay, { opacity: 0, y: -40 },
              { opacity: 1, y: 0, duration: MHERO.titleFadeOut, ease: 'power2.out' }, '>-0.2');
          } else {
            // BRIDGE <-> BRIDGE crossfade (states 1..4).
            var outB = mhBeatFor(from);
            var inB  = mhBeatFor(to);
            if (fwd) {
              if (outB) tl.to(outB, { opacity: 0, y: -40, duration: MHERO.bridgeOut, ease: 'power2.in' }, 0);
              if (inB)  tl.fromTo(inB, { opacity: 0, y: 40 },
                { opacity: 1, y: 0, duration: MHERO.bridgeIn, ease: 'power2.out' }, 0.15);
            } else {
              if (outB) tl.to(outB, { opacity: 0, y: 40, duration: MHERO.bridgeOut, ease: 'power2.in' }, 0);
              if (inB)  tl.fromTo(inB, { opacity: 0, y: -40 },
                { opacity: 1, y: 0, duration: MHERO.bridgeIn, ease: 'power2.out' }, 0.15);
            }
          }
        }

        // ---- Scroll lock / release ----
        // STEPPING : scroll fully suppressed, hero pin in front (absolute).
        // RELEASED : hero pin goes fixed BEHIND the page (CSS class), and
        //            the rest of the content slides up OVER it on native
        //            scroll — the hero "stays put."
        function mhPreventTouch(e) {
          if (mhReleased) return;
          // NEVER interfere with multi-touch (pinch-zoom). Only the
          // single-finger stepping swipe is ours. This keeps the browser's
          // native zoom working and prevents the page getting stuck zoomed.
          if (e.touches && e.touches.length > 1) return;
          // At the LAST state, let a forward drag (finger up) pass straight
          // through to native scroll AND release — so a single swipe both
          // unlocks and slides the content up, instead of taking two.
          if (mhStep >= MH_LAST && mhStartY !== null) {
            var t = e.touches && e.touches[0];
            if (t && (t.clientY - mhStartY) < 0) { mhRelease(); return; }
          }
          e.preventDefault();
        }

        function mhEnterStepping() {
          mhReleased = false;
          heroEl.classList.remove('ss-hero-released');
          heroEl.classList.remove('ss-hero-offscreen');
          window.scrollTo(0, 0);
          document.addEventListener('touchmove', mhPreventTouch, { passive: false });
          if (window.__ssLenis && typeof window.__ssLenis.stop === 'function') window.__ssLenis.stop();
        }

        function mhRelease() {
          if (mhReleased) return;
          mhReleased = true;
          heroEl.classList.add('ss-hero-released');
          document.removeEventListener('touchmove', mhPreventTouch, { passive: false });
          if (window.__ssLenis && typeof window.__ssLenis.start === 'function') window.__ssLenis.start();
        }
        window.__ssReleaseHero = mhRelease;

        // mhPastHero — are we scrolled below the hero? Drives the initial
        // lock decision AND the touch self-heal below.
        function mhPastHero() {
          return (window.scrollY || window.pageYOffset || 0) > (heroEl.offsetHeight || window.innerHeight) * 0.5;
        }

        // Don't let the browser restore scroll mid-page on this stepped page.
        // ROOT CAUSE of the pinch lockup: an iOS memory-reload (the white
        // flash after a pinch-zoom) restored the user deep in the content,
        // but the hero controller re-init locked at step 0 — freezing scroll
        // until you swiped through the (now invisible) hero. Forcing top on
        // load + the self-heal below makes that impossible.
        if ('scrollRestoration' in history) { try { history.scrollRestoration = 'manual'; } catch (e) {} }

        // Lock + run the stepped intro only when we're at the top. If we ever
        // load/restore below the hero, start RELEASED so scroll just works.
        // If we arrived via a ?goto= deep link, start RELEASED so the stepped
        // hero lock doesn't block the programmatic scroll to the target.
        var _ssGotoEl = ssGoto ? document.getElementById(ssGoto) : null;
        if (mhPastHero() || _ssGotoEl) { mhRelease(); } else { mhEnterStepping(); }
        // Backstops for late iOS scroll restoration.
        window.addEventListener('load', function () { if (!mhReleased && mhPastHero()) mhRelease(); });
        setTimeout(function () { if (!mhReleased && mhPastHero()) mhRelease(); }, 500);

        // Once released, the hero stays released for the rest of the page —
        // NO auto re-lock. (Re-locking when scrollY returned to the top was
        // trapping the page, especially around pinch-zoom, where the visual
        // viewport reports y≈0 and the lock re-engaged mid-gesture.) We only
        // toggle the fixed pin's visibility here: hide it once the user is
        // fully past the hero so it can't peek through on a bottom overscroll
        // bounce; show it again inside the slide-over zone.
        window.addEventListener('scroll', function () {
          if (!mhReleased) return;
          var y = window.scrollY || window.pageYOffset || 0;
          var heroH = heroEl.offsetHeight || window.innerHeight;
          if (y > heroH) heroEl.classList.add('ss-hero-offscreen');
          else heroEl.classList.remove('ss-hero-offscreen');
        }, { passive: true });

        // ---- Gesture detection: one swipe = exactly one step ----
        var mhStartY   = null;
        var mhConsumed = false;
        window.addEventListener('touchstart', function (e) {
          // SELF-HEAL: if we're locked but actually scrolled past the hero
          // (an iOS reload re-locked us deep in the page), free scrolling on
          // the very first touch — the page can never stay stuck.
          if (!mhReleased && mhPastHero()) { mhRelease(); return; }
          if (mhReleased) return;
          var t = e.touches && e.touches[0];
          if (!t) return;
          mhStartY = t.clientY;
          mhConsumed = false;
        }, { passive: true });

        window.addEventListener('touchmove', function (e) {
          if (mhReleased || mhConsumed || mhAnimating || mhStartY === null) return;
          if (e.touches && e.touches.length > 1) return;  // ignore pinch-zoom
          var t = e.touches && e.touches[0];
          if (!t) return;
          var delta = t.clientY - mhStartY;   // finger up => delta < 0 => forward
          if (Math.abs(delta) < MHERO.swipePx) return;
          mhConsumed = true;
          if (delta < 0) {
            // FORWARD — advance one state; at the last state, release to the site.
            if (mhStep >= MH_LAST) { mhRelease(); return; }
            mhGo(mhStep + 1);
          } else {
            // BACKWARD — retreat one state (never past the title).
            if (mhStep > 0) mhGo(mhStep - 1);
          }
        }, { passive: true });

        window.addEventListener('touchend', function () {
          mhStartY = null;
          mhConsumed = false;
        }, { passive: true });
      }

      // === MOBILE KEN BURNS: scroll-zoom on each frame image ===
      // As each frame scrolls through the viewport, its image zooms in.
      // Focal point per frame (transform-origin set in CSS via .ss-kb[data-kb]):
      // F1 left, F2 left, F3 right, F4 center. The .ss-kb wrapper clips the
      // overflow so it reads as a contained Ken Burns push-in. Mobile only —
      // desktop gets its own treatment.
      if (isMobile && !reduced && window.gsap && window.ScrollTrigger) {
        document.querySelectorAll('.ss-kb').forEach(function (box) {
          var kbImg = box.querySelector('img');
          if (!kbImg) return;
          window.gsap.fromTo(kbImg, { scale: 1 }, {
            scrollTrigger: { trigger: box, start: 'top bottom', end: 'bottom top', scrub: 0.5 },
            scale: 1.5,   // strong mobile zoom — really moves as you scroll
            ease: 'none'
          });
        });
      }

      // === QUOTE INTERLUDES: fade up between features ===
      var quotes = document.querySelectorAll('[data-ss-quote]');
      if (quotes.length && !reduced) {
        quotes.forEach(function (q) {
          var text = q.querySelector('[data-ss-quote-text]');
          var attr = q.querySelector('[data-ss-quote-attr]');
          var els = [text, attr].filter(Boolean);
          if (!els.length) return;
          window.gsap.from(els, {
            scrollTrigger: {
              trigger: q,
              start: 'top 75%',
              end: 'top 30%',
              scrub: 0.5
            },
            y: 36,
            opacity: 0,
            stagger: 0.15,
            ease: 'power2.out'
          });
        });
      }

      // === DETAIL: scroll-scrubbed canvas scene ===
      // Placeholder: zooms a single image based on scroll progress.
      // When the real frame sequence is delivered, populate the
      // <script type="application/json" data-ss-detail-frames>[...]</script>
      // tag in ss-launch-detail.liquid with frame URLs and this code will
      // automatically scrub through them instead of zooming.
      var detailCanvas = document.querySelector('[data-ss-detail-canvas]');
      var detailScene = document.querySelector('.ss-launch-detail');
      if (detailCanvas && detailScene) {
        var ctx = detailCanvas.getContext('2d');
        var dpr = window.devicePixelRatio || 1;
        var placeholderImg = new Image();
        var placeholderReady = false;
        placeholderImg.crossOrigin = 'anonymous';
        placeholderImg.src = detailCanvas.getAttribute('data-placeholder');
        placeholderImg.onload = function () {
          placeholderReady = true;
          sizeCanvas();
          drawDetail(0);
        };

        // Real-frame-mode plumbing — loads if a frames JSON tag is present
        var framesEl = document.querySelector('[data-ss-detail-frames]');
        var frameImages = [];
        var framesReady = false;
        if (framesEl) {
          try {
            var frameUrls = JSON.parse(framesEl.textContent);
            var loaded = 0;
            frameUrls.forEach(function (url, idx) {
              var img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = function () {
                loaded++;
                if (loaded === frameUrls.length) {
                  framesReady = true;
                  drawDetail(0);
                }
              };
              img.src = url;
              frameImages[idx] = img;
            });
          } catch (e) {
            // Bad JSON — fall back to placeholder
          }
        }

        function sizeCanvas() {
          var rect = detailCanvas.getBoundingClientRect();
          detailCanvas.width = Math.round(rect.width * dpr);
          detailCanvas.height = Math.round(rect.height * dpr);
        }

        function drawDetail(progress) {
          if (!ctx) return;
          var w = detailCanvas.width;
          var h = detailCanvas.height;
          ctx.fillStyle = '#000';
          ctx.fillRect(0, 0, w, h);

          if (framesReady && frameImages.length) {
            // REAL MODE: pick the right frame
            var idx = Math.min(frameImages.length - 1, Math.floor(progress * (frameImages.length - 1)));
            var img = frameImages[idx];
            if (img && img.complete) {
              drawCover(img, w, h);
            }
            return;
          }

          if (!placeholderReady) return;

          // PLACEHOLDER MODE: progressive zoom-in
          var minScale = 0.55;
          var maxScale = 1.25;
          var scale = minScale + (maxScale - minScale) * progress;
          drawContained(placeholderImg, w, h, scale);
        }

        function drawCover(img, w, h) {
          // Cover behavior — fill canvas, crop excess
          var imgRatio = img.width / img.height;
          var canvasRatio = w / h;
          var sw, sh;
          if (imgRatio > canvasRatio) {
            sh = img.height;
            sw = sh * canvasRatio;
          } else {
            sw = img.width;
            sh = sw / canvasRatio;
          }
          var sx = (img.width - sw) / 2;
          var sy = (img.height - sh) / 2;
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
        }

        function drawContained(img, w, h, scale) {
          // Contain behavior — fit inside canvas, scaled
          var size = Math.min(w, h) * scale;
          var aspect = img.width / img.height;
          var dw, dh;
          if (aspect > 1) {
            dw = size;
            dh = size / aspect;
          } else {
            dh = size;
            dw = size * aspect;
          }
          var dx = (w - dw) / 2;
          var dy = (h - dh) / 2;
          ctx.drawImage(img, dx, dy, dw, dh);
        }

        // Resize handler
        var resizeTimer;
        window.addEventListener('resize', function () {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(function () {
            sizeCanvas();
            // Redraw at current progress
            var st = window.ScrollTrigger.getById('detailScrub');
            drawDetail(st ? st.progress : 0);
          }, 150);
        });

        // ScrollTrigger drives the canvas progress
        window.ScrollTrigger.create({
          id: 'detailScrub',
          trigger: detailScene,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.5,
          onUpdate: function (self) {
            drawDetail(self.progress);
          }
        });

        // Heading entrance
        var detailOverlay = document.querySelector('.ss-launch-detail__overlay');
        if (detailOverlay && !reduced) {
          window.gsap.from(detailOverlay.children, {
            scrollTrigger: {
              trigger: detailScene,
              start: 'top 75%',
              end: 'top 30%',
              scrub: 0.5
            },
            opacity: 0,
            y: 30,
            stagger: 0.1
          });
        }
      }
    }

    // --- Counter toggle (built but hidden — flip via data attribute) ---
    // To reveal: set [data-ss-counter-enabled] on <body class="ss-spartan-body">
    // and remove the [hidden] attribute on .ss-launch-stickybuy__counter
    var body = document.body;
    if (body && body.hasAttribute('data-ss-counter-enabled')) {
      var counter = document.querySelector('[data-ss-counter]');
      if (counter) counter.removeAttribute('hidden');
    }
  });
})();
